import { useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import { Stage } from '@/components/Stage/Stage';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import ChartChartArea from '@/features/(charts)/chart-renderer/ui/chart-chart-area';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';
import color from '@/styles/colors/color';
import { track } from '@/utils/track/track';

import useCustomChartState from '../../model/use-custom-chart-state';
import CustomChartCandidatesTable from './candidates-table';
import CustomChartDataset from './dataset';
import CustomChartTitle from './title';

type Props = {
  issue: CompositedSignalIssue;
  datePreset: SignalDateSelectionInput;
  filterSet: StructuredFilter;
};

export const CustomChartArea = (props: Props) => {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const chartInfo = useCustomChartState({
    filterSet: props.filterSet,
    issue: props.issue,
    datePreset: props.datePreset,
  });

  // Track chart field changes
  const prevStateRef = useRef(chartInfo.state);
  useEffect(() => {
    const prev = prevStateRef.current;
    const curr = chartInfo.state;

    if (
      prev.field.type !== curr.field.type ||
      prev.graph !== curr.graph ||
      prev.graph_direction !== curr.graph_direction
    ) {
      track(AMP_KEYS.SIGNAL_CUSTOM_CHART_FIELD_CHANGE, {
        issue_name: props.issue.name,
        field_type: curr.field.type,
        field_items_count: curr.selected_field.items?.length ?? 0,
        graph_direction: curr.graph_direction,
        graph: curr.graph,
      });
    }
    prevStateRef.current = curr;
  }, [chartInfo.state, props.issue.name]);

  return (
    <div className="space-y-4">
      <CustomChartTitle chartInfo={chartInfo} />
      <CustomChartDataset chartInfo={chartInfo} />
      <Stage className="min-h-75 w-full relative">
        <Stage.Scene
          status={chartInfo.tableQuery.isFetching}
          className="flex items-center justify-center h-75"
        >
          <Spinner />
        </Stage.Scene>
        <Stage.Scene status={chartInfo.tableQuery.isFetched}>
          <div className="space-y-4">
            {Number(chartInfo.tableQuery.data?.tables?.length) > 0 ? (
              <ChartChartArea
                chartInfo={chartInfo}
                yAxisTitle={replaceFeedbackTerminology('Feedback count')}
              />
            ) : (
              <EmptyChartFallback />
            )}

            {/*
             * FIXME: ErrorBoundary가 useChartCandidateTable hooks의 에러를 무시하게 만들어줌.
             * 1. ErrorBoundary로 유저 플로우를 만들지 않도록 수정 필요
             * 2. useChartCandidateTable hooks의 에러를 처리하는 로직 변경 필요
             */}
            <ErrorBoundary
              key={JSON.stringify(chartInfo)}
              fallback={<div className="h-[280px]"></div>}
            >
              <CustomChartCandidatesTable chartInfo={chartInfo} />
            </ErrorBoundary>
          </div>
        </Stage.Scene>
      </Stage>
    </div>
  );
};

const EmptyChartFallback = () => {
  return (
    <CenterFlex
      f={1}
      className="w-full aspect-[2/1] min-h-[400px] rounded-2xl border border-border"
    >
      <Typo type="label3" style={{ color: color.gray600 }}>
        Select data sets and apply various filters to create your own chart.
      </Typo>
    </CenterFlex>
  );
};
